
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import apiClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";

const TEMPLATE_FILENAME = "Service Provider of the Year Award_2025.xlsx";

const rowMapping = {
  organisationname: 5,


 no_project:8,
 no_project_awarded_2024:11,
 no_project_awarded_2023:12,

 value_project:15,
 value_project_awarded_2024:18,
 value_project_awarded_2023:19,

no_project_completed_2024:25,
no_project_completed_time_overrun_2024:26,
no_project_completed_cost_overrun_2024:27,


new_technologies:30,
value_technologies:32,

fatal_accident:35,
lost_time:36,
incident:39,

};

/** Helpers for Excel columns / range */
const colLetter = (colIndex) => {
  let letters = "";
  while (colIndex >= 0) {
    letters = String.fromCharCode((colIndex % 26) + 65) + letters;
    colIndex = Math.floor(colIndex / 26) - 1;
  }
  return letters;
};

const colIndexFromLetter = (col) => {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 65 + 1);
  }
  return index - 1;
};

const expandSheetRange = (sheet, minCol, minRow, maxCol, maxRow) => {
  const start = `${colLetter(minCol)}${minRow}`;
  const end = `${colLetter(maxCol)}${maxRow}`;
  sheet["!ref"] = `${start}:${end}`;
};

const ExcelSP = () => {
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exported, setExported] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiClient.get("service-provider/list/");
        const results = (resp.data && (resp.data.results ?? resp.data)) || [];
        console.log("API results length:", results.length);
        setApiData(results);
      } catch (err) {
        console.error("API fetch error:", err);
        setError("Failed to fetch API data. See console.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading && !error && apiData && apiData.length > 0 && !exported) {
      (async () => {
        try {
          await performExport(apiData);
          setExported(true);
        } catch (err) {
          console.error("Auto-export failed:", err);
        }
      })();
    }
  }, [loading, error, apiData, exported]);

  // Helper that tries many field names
  const getVal = (obj, ...keys) => {
    for (const k of keys) {
      if (!k) continue;
      const val = obj[k];
      if (val !== undefined && val !== null) return val;
    }
    return undefined;
  };

  // resolve merges: if (r,c) lies inside a merged range, return top-left of that merge
  const resolveMergeCell = (sheet, r, c) => {
    const merges = sheet["!merges"] || [];
    for (const m of merges) {
      if (r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c) {
        return { r: m.s.r, c: m.s.c };
      }
    }
    return { r, c };
  };

  const performExport = async (dataToWrite) => {
    if (!dataToWrite || dataToWrite.length === 0) {
      throw new Error("No API data to export.");
    }

    const url = encodeURI(`${process.env.PUBLIC_URL || ""}/templates/${TEMPLATE_FILENAME}`);
    console.log("Fetching template:", url);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Template not found (status ${resp.status})`);

    const contentType = resp.headers.get("content-type") || "";
    if (contentType.includes("text/html")) {
      const txt = await resp.text();
      console.error("Template fetch returned HTML (first 300 chars):", txt.slice(0, 300));
      throw new Error("Template returned HTML. Check file path/name.");
    }

    const arrayBuffer = await resp.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    console.log("Using sheet:", sheetName, "initial !ref:", sheet["!ref"]);

    // existing bounds
    const existingRef = sheet["!ref"] || "A1:A1";
    const [startRef, endRef] = existingRef.split(":");
    const startColLetter = (startRef || "A1").replace(/[0-9]/g, "");
    const startRow = parseInt((startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;
    const endColLetter = (endRef || startRef || "A1").replace(/[0-9]/g, "");
    const endRow = parseInt((endRef || startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;

    let minCol = colIndexFromLetter(startColLetter || "A");
    let minRow = startRow;
    let maxCol = colIndexFromLetter(endColLetter || "A");
    let maxRow = endRow;

    // Choose start column (zero-based). Change START_COL if you want to begin at G (6) or somewhere else.
    const START_COL = 9; // 6 => column G. Adjust if your template expects a different starting column.

    dataToWrite.forEach((company, idx) => {
      const colIndex = START_COL + idx; // zero-based
      const col = colLetter(colIndex);
      console.log(`\n--- Writing company #${idx + 1} into column ${col} (zero-based ${colIndex}) ---`);
      console.log("Company raw keys:", Object.keys(company).slice(0,50)); // quick peek

      // attempt multiple key names for each field to be resilient to API changes
      const organisationname = getVal(company, "organisation_name", "organisationname", "org_name", "organisation");
      const no_project = getVal(company, "projects_in_progress", "projects_in_progress", "projects_in_progress");
      const no_project_awarded_2024 = getVal(company, "projects_awarded_2024", "projects_awarded_2024");
      const no_project_awarded_2023 = getVal(company, "projects_awarded_2023");
      const value_project = getVal(company, "projects_in_progress_value", "projects_in_progress_value");
      const value_project_awarded_2024 = getVal(company, "projects_value_2024");
      const value_project_awarded_2023 = getVal(company, "projects_value_2023", "projects_value_2023");
      const no_project_completed_2024 = getVal(company, "projects_completed");
      const no_project_completed_time_overrun_2024 = getVal(company, "projects_no_time_overrun");
      const no_project_completed_cost_overrun_2024 = getVal(company, "projects_no_cost_overrun");
      const new_technologies = getVal(company, "technologies_introduced", "technologies_introduced");
      const value_technologies = getVal(company, "technologies_value", "technologies_value");
      const fatal_accident = getVal(company, "fatalities");
      const lost_time = getVal(company, "lost_time_injuries");
      const incident = getVal(company, "osha_incidents");

      const writes = [
        [rowMapping.organisationname, organisationname, "organisationname"],
        [rowMapping.no_project, no_project, "no_project"],
        [rowMapping.no_project_awarded_2024, no_project_awarded_2024, "no_project_awarded_2024"],
        [rowMapping.no_project_awarded_2023, no_project_awarded_2023, "no_project_awarded_2023"],
        [rowMapping.value_project, value_project, "value_project"],
        [rowMapping.value_project_awarded_2024, value_project_awarded_2024, "value_project_awarded_2024"],
        [rowMapping.value_project_awarded_2023, value_project_awarded_2023, "value_project_awarded_2023"],
        [rowMapping.no_project_completed_2024, no_project_completed_2024, "no_project_completed_2024"],
        [rowMapping.no_project_completed_time_overrun_2024, no_project_completed_time_overrun_2024, "no_project_completed_time_overrun_2024"],
        [rowMapping.no_project_completed_cost_overrun_2024, no_project_completed_cost_overrun_2024, "no_project_completed_cost_overrun_2024"],
        [rowMapping.new_technologies, new_technologies, "new_technologies"],
        [rowMapping.value_technologies, value_technologies, "value_technologies"],
        [rowMapping.fatal_accident, fatal_accident, "fatal_accident"],
        [rowMapping.lost_time, lost_time, "lost_time"],
        [rowMapping.incident, incident, "incident"],
      ];

      writes.forEach(([rowNum, value, fieldName]) => {
        if (rowNum == null) return; // missing mapping
        const rZero = rowNum - 1;
        const cZero = colIndex;

        // resolve merges (if writing inside a merged cell, write to master)
        const { r: rResolved, c: cResolved } = resolveMergeCell(sheet, rZero, cZero);

        // If value is undefined/null -> set to empty string but log it
        let writeVal = value;
        if (writeVal === undefined || writeVal === null || writeVal === "") {
          console.warn(`Company #${idx + 1} field "${fieldName}" missing -> writing empty string to ${colLetter(cResolved)}${rResolved + 1}`);
          writeVal = "";
        }

        // Try to coerce numeric-looking strings to numbers
        if (typeof writeVal === "string") {
          const trimmed = writeVal.trim();
          if (trimmed !== "" && /^-?\d+(\.\d+)?$/.test(trimmed)) {
            writeVal = Number(trimmed);
          }
        }

        // Use sheet_add_aoa with { origin: { r, c } } for robust placement
        try {
          XLSX.utils.sheet_add_aoa(sheet, [[writeVal]], { origin: { r: rResolved, c: cResolved } });
          const cellAddress = `${colLetter(cResolved)}${rResolved + 1}`;
          const newCell = sheet[cellAddress];
          if (newCell) {
            if (typeof writeVal === "number") newCell.t = "n";
            else if (typeof writeVal === "boolean") newCell.t = "b";
            else newCell.t = "s";
          }
          console.log(`WROTE ${fieldName} -> ${colLetter(cResolved)}${rResolved + 1} =`, writeVal);
        } catch (e) {
          console.error("Write failed for", fieldName, "at", rResolved, cResolved, e);
          // fallback: write raw cell object
          const fallbackCellRef = `${colLetter(cZero)}${rowNum}`;
          sheet[fallbackCellRef] = { t: typeof writeVal === "number" ? "n" : "s", v: writeVal };
        }

        if (cZero > maxCol) maxCol = cZero;
        if (rowNum > maxRow) maxRow = rowNum;
      });
    });

    // update sheet range so Excel shows new cells
    expandSheetRange(sheet, minCol, minRow, Math.max(maxCol, minCol), Math.max(maxRow, minRow));
    console.log("Updated sheet !ref:", sheet["!ref"]);

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Modified_${TEMPLATE_FILENAME}`);
    console.log("Excel exported successfully.");
  };

  if (loading) return <div>Loading API data...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  return <><>{navigate("/Thankyou")}</></>;
};

export default ExcelSP;
